import { MdLockOutline, MdOutlineGroup } from 'react-icons/md';

import { DateTimePicker, Modal } from '@/components';
import { Button, ButtonOutlined } from '@/components/Button';
import { Input, Select } from '@/components/Inputs';
import { RichTextArea } from '@/components/RichTextArea/RichTextArea';
import type { TaskStatus } from '@/store/tasks/tasks.dto';

import { TASK_EQUIPMENT, TASK_REPEATS, TASK_STATUSES, TASK_TYPES } from '../eventWizard.constants';
import { fromIsoValue, toIsoValue } from '../eventWizard.utils';
import s from '../EventWizardModal.module.scss';

const TOTAL_TASK_STEPS = 2;

interface TaskFormModalProps {
  open: boolean;
  eventStart: Date | null;
  eventEnd: Date | null;
  onClose: () => void;
  eventTitle: string;
  taskStep: number;
  onTaskStepChange: (step: number) => void;
  taskTitle: string;
  onTaskTitleChange: (value: string) => void;
  taskPrivate: boolean;
  onTaskPrivateToggle: () => void;
  taskStatus: TaskStatus;
  onTaskStatusChange: (value: TaskStatus) => void;
  taskType: string;
  onTaskTypeChange: (value: string) => void;
  taskDate: Date | null;
  taskEndDate: Date | null;
  onTaskDatesChange: (start: Date | null, end: Date | null) => void;
  taskEquipment: string;
  onTaskEquipmentChange: (value: string) => void;
  taskDescription: string;
  onTaskDescriptionChange: (value: string) => void;
  taskRepeat: string;
  onTaskRepeatChange: (value: string) => void;
  onSave: () => void;
}

export function TaskFormModal({
  open,
  eventStart,
  eventEnd,
  onClose,
  eventTitle,
  taskStep,
  onTaskStepChange,
  taskTitle,
  onTaskTitleChange,
  taskPrivate,
  onTaskPrivateToggle,
  taskStatus,
  onTaskStatusChange,
  taskType,
  onTaskTypeChange,
  taskDate,
  taskEndDate,
  onTaskDatesChange,
  taskEquipment,
  onTaskEquipmentChange,
  taskDescription,
  onTaskDescriptionChange,
  taskRepeat,
  onTaskRepeatChange,
  onSave,
}: TaskFormModalProps) {
  return (
    <Modal open={open} onClose={onClose}>
      <div className={s.taskWizard}>
        <div className={s.taskWizardHead}>
          <div className={s.taskWizardHeadRow}>
            <span className={s.taskWizardTitle}>Add Task</span>
            <span className={s.taskWizardStep}>
              Step {taskStep} of {TOTAL_TASK_STEPS}
            </span>
          </div>
          <span className={s.taskWizardName}>
            <span className={s.taskWizardCrumbEvent}>{eventTitle.trim() || 'Untitled event'}</span>
            <span className={s.taskWizardCrumbSep}>›</span>
            <span className={s.taskWizardCrumbTask}>{taskTitle.trim() || 'Add Task'}</span>
          </span>
          <div className={s.taskWizardSegments}>
            {Array.from({ length: TOTAL_TASK_STEPS }, (_, i) => i + 1).map((n) => (
              <span
                key={n}
                className={`${s.taskWizardSegment} ${n <= taskStep ? s.taskWizardSegmentActive : ''}`}
              />
            ))}
          </div>
        </div>

        {taskStep === 1 && (
          <div key={taskStep} className={s.taskWizardStepBody}>
            <div className={s.taskFormToggle}>
              <span className={s.taskPrivateIcon}>
                {taskPrivate ? <MdLockOutline /> : <MdOutlineGroup />}
              </span>
              <span className={s.taskFormToggleLabel}>
                {taskPrivate
                  ? 'This task is private, so only you can see it'
                  : 'This task is visible to all crew members.'}
              </span>
              <button
                type="button"
                className={`${s.toggleTrack} ${!taskPrivate ? s.toggleTrackOn : ''}`}
                onClick={onTaskPrivateToggle}
                aria-label="Toggle visibility"
              >
                <span className={s.toggleThumb} />
              </button>
            </div>

            <Input
              label="Name"
              mandatory
              value={taskTitle}
              onChange={(e) => onTaskTitleChange(e.target.value)}
              placeholder="Check the tender fuel"
            />

            <div className={s.taskFormRow}>
              <Select<TaskStatus>
                label="Status"
                placeholder="Status"
                options={TASK_STATUSES}
                value={taskStatus}
                onChange={onTaskStatusChange}
              />
              <Select<string>
                label="Type"
                placeholder="Type"
                options={TASK_TYPES}
                value={taskType}
                onChange={onTaskTypeChange}
              />
            </div>

            <DateTimePicker
              label="Dates"
              placeholder="Choose dates"
              minDate={eventStart ?? undefined}
              maxDate={eventEnd ?? undefined}
              value={toIsoValue(taskDate)}
              endValue={toIsoValue(taskEndDate)}
              onRangeChange={(start, end) =>
                onTaskDatesChange(fromIsoValue(start), fromIsoValue(end))
              }
            />
          </div>
        )}

        {taskStep === 2 && (
          <div key={taskStep} className={s.taskWizardStepBody}>
            <Select<string>
              label="Equipment"
              placeholder="Equipment"
              options={TASK_EQUIPMENT}
              value={taskEquipment}
              onChange={onTaskEquipmentChange}
            />

            <RichTextArea
              label="Description"
              value={taskDescription}
              onChange={onTaskDescriptionChange}
              placeholder="Top up before the morning run"
            />

            <Select<string>
              label="Repeat"
              placeholder="Repeat"
              options={TASK_REPEATS}
              value={taskRepeat}
              onChange={onTaskRepeatChange}
            />
          </div>
        )}

        <div className={s.taskFormActions}>
          {taskStep > 1 ? (
            <ButtonOutlined onClick={() => onTaskStepChange(taskStep - 1)}>Back</ButtonOutlined>
          ) : (
            <ButtonOutlined onClick={onClose}>Cancel</ButtonOutlined>
          )}
          {taskStep < TOTAL_TASK_STEPS ? (
            <Button
              onClick={() => onTaskStepChange(taskStep + 1)}
              disabled={taskStep === 1 && !taskTitle.trim()}
            >
              Next
            </Button>
          ) : (
            <Button onClick={onSave} disabled={!taskTitle.trim()}>
              Save
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
